/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
  TrackingEventType,
  TrackingEventData,
  TrackerFunction,
} from "@huub-web/ui";

import { redactPII } from "./utils/redactPII";

export type CreateGTMTrackerFn = (
  publish?: (event: object) => void,
) => TrackerFunction;

/**
 * Creates a GTM tracker that transforms semantic events to GTM's expected format
 */
export const createGTMTracker: CreateGTMTrackerFn =
  <T extends TrackingEventType>(publish?: (event: object) => void) =>
  (event: T, data?: TrackingEventData<T>) => {
    if (typeof window === "undefined") {
      return;
    }

    let gtmEvent: Record<string, any> = {};

    // Transform semantic events to GTM format
    if (event === "pageView") {
      const pageData = data as TrackingEventData<"pageView">;
      gtmEvent = {
        event: "pageView",
        page: pageData?.pagePath,
        title: pageData?.pageTitle,
        referrer: pageData?.referrer,
        ...pageData?.properties,
      };
    } else if (event === "interaction") {
      const interactionData = data as TrackingEventData<"interaction">;

      // Map interaction categories to specific GTM events
      if (
        interactionData?.category === "button" &&
        interactionData?.action === "click"
      ) {
        gtmEvent = {
          event: "buttonClick",
          value: interactionData.label,
          buttonId: interactionData.properties?.buttonId,
          buttonText: interactionData.properties?.buttonText,
          buttonType: interactionData.properties?.buttonType,
          ...interactionData.properties,
        };
      } else if (
        interactionData?.category === "link" &&
        interactionData?.action === "click"
      ) {
        gtmEvent = {
          event: "linkClick",
          value: interactionData.label,
          linkUrl: interactionData.properties?.linkUrl,
          linkText: interactionData.properties?.linkText,
          ...interactionData.properties,
        };
      } else if (
        interactionData?.category === "form" &&
        interactionData?.action === "submit"
      ) {
        gtmEvent = {
          event: "formSubmit",
          value: interactionData.label,
          formId: interactionData.properties?.formId,
          formType: interactionData.properties?.formType,
          ...interactionData.properties,
        };
      } else if (interactionData?.category === "select") {
        gtmEvent = {
          event: "select",
          value: interactionData.label,
          itemType: interactionData.properties?.itemType,
          itemId: interactionData.properties?.itemId,
          ...interactionData.properties,
        };
      } else if (interactionData?.category === "search") {
        gtmEvent = {
          event: "search",
          value: interactionData.label,
          query: interactionData.properties?.query,
          searchType: interactionData.properties?.searchType,
          ...interactionData.properties,
        };
      } else if (interactionData?.category === "video") {
        gtmEvent = {
          event: "video",
          action: interactionData.action,
          value: interactionData.label,
          videoId: interactionData.properties?.videoId,
          ...interactionData.properties,
        };
      } else {
        // Generic interaction
        gtmEvent = {
          event: "interaction",
          category: interactionData?.category,
          action: interactionData?.action,
          label: interactionData?.label,
          value: interactionData?.value,
          ...interactionData?.properties,
        };
      }
    } else if (event === "custom") {
      const customData = data as TrackingEventData<"custom">;
      gtmEvent = {
        event: customData?.category || "custom",
        action: customData?.action,
        label: customData?.label,
        value: customData?.value,
        ...customData?.properties,
      };
    } else {
      // Fallback for any other events
      gtmEvent = {
        event,
        ...data,
      };
    }

    // Redact PII before it leaves for GTM (US processor) — single egress
    // chokepoint for all GTM events (SMA-2670). Drops identifying keys and
    // scrubs embedded emails/BSN/IBAN from surviving values.
    const safeEvent = redactPII(gtmEvent);

    // Push to dataLayer
    if (publish) {
      publish(safeEvent);
    } else {
      (window as any).dataLayer?.push(safeEvent);
    }
  };
